import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Stack } from '@mui/material';
import { Icon } from '@iconify/react';
import { getOgdTeamColorById } from '../../../../utils/constants/functions';
import NoDataFound from '../../../../components/NodataIndication/NoDataFound';
import Loader3D from '../../../../components/Loader/Loader3D';
import { useDispatch } from 'react-redux';
import { perfomanceOverview } from '../../../../redux/features/poinTrackerSlice';
import { useSelector } from 'react-redux';
import { ogdWings } from '../../../../utils/constants/GlobalStaticData';
import dayjs from 'dayjs';
import { restrict2digitAfterDecimal } from '../../../../utils/helperFunction';

const METRIC_COMPONENTS = {
  SFR: {
    icon: 'mdi:chart-bar',
    color: '#16a34a',
    label: 'SFR',
  },
  TAR: {
    icon: 'mdi:target',
    color: '#2563eb',
    label: 'TAR',
  },
  ACR: {
    icon: 'mdi:checkbox-marked',
    color: '#9333ea',
    label: 'ACR',
  },
  SQS: {
    icon: 'mdi:chart-line',
    color: '#64748b',
    label: 'SQs',
  },
  ADM: {
    icon: 'mdi:account-group',
    color: '#0891b2',
    label: 'Adm',
    sub: 'Admissions Count',
  },
  REV: {
    icon: 'mdi:currency-inr',
    color: '#4f46e5',
    label: 'Revenue',
    sub: 'Total Collection',
  },
};

const TeamPerfomanceOverview = ({parentDate}) => {
  const previousDate = useRef(parentDate);
  const teamPerfomanceOverview = useSelector((state) => state.pointTracker.teamPerfomanceOverview);
  const HEADERS = teamPerfomanceOverview?.responseData?.meta?.headers ||ogdWings
  const loading = teamPerfomanceOverview?.loading;
  const METRIC_NAMES = teamPerfomanceOverview?.responseData?.meta?.metricNames || [
    'SFR',
    'TAR',
    'ACR',
    'SQS',
    'ADM',
    'REV',
  ];
  const METRIC_DATA = teamPerfomanceOverview?.responseData?.data || {};
  const noDatafound = Object.keys(teamPerfomanceOverview?.responseData?.data || {}).length === 0;
  const dispatch = useDispatch();
  useEffect(() => {
    if (Object.keys(teamPerfomanceOverview?.responseData || {}).length === 0 || previousDate.current !== parentDate) {
      dispatch(perfomanceOverview({ date:parentDate }));
      previousDate.current = parentDate;
    }
  }, [parentDate,dispatch,teamPerfomanceOverview?.responseData]);
  return (
    <Paper
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
          color: '#fff',
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
            }}
          >
            <Icon icon="mdi:trophy-outline" width={24} />
          </Box>

          <Box>
            <Typography fontWeight={600}>Team Performance Overview</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Comprehensive metrics across all teams
            </Typography>
          </Box>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <Loader3D />
        </Box>
      ) : noDatafound ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <NoDataFound />
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: `${180 + HEADERS.length * 210}px`, width: 'fit-content' }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `180px repeat(${HEADERS.length}, 210px)`,
                  p: 2,
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  alignItems: 'center',
                }}
              >
                <Typography fontWeight={600}>METRIC</Typography>

                {HEADERS.map((h, i) => {
                  const { color, bgColor } = getOgdTeamColorById(h.id);

                  return (
                    <Typography
                      key={i}
                      textAlign="center"
                      fontWeight={600}
                      sx={{
                        color,
                        backgroundColor: bgColor,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block',
                        mx: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h.value}
                    </Typography>
                  );
                })}
              </Box>

              {METRIC_NAMES.map((name, i) => {
                const meta = METRIC_COMPONENTS[name?.toUpperCase()];
                const currentMetricData = METRIC_DATA[name?.toUpperCase()];

                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `180px repeat(${HEADERS.length}, 210px)`,
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid #f1f5f9',
                      '&:hover': { backgroundColor: '#f9fafb' },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          backgroundColor: meta.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        <Icon icon={meta.icon} width={20} />
                      </Box>

                      <Box>
                        <Typography fontWeight={600}>{meta.label}</Typography>
                        {meta.sub && (
                          <Typography variant="caption" color="text.secondary">
                            {meta.sub}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {HEADERS.map((val, idx) => {
                      const rowData = restrict2digitAfterDecimal(currentMetricData[val.id]) || 0;
                      const { color = '#e5e7eb', bgColor = '#000' } = getOgdTeamColorById(val.id);

                      return (
                        <Box key={idx} textAlign="center">
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '8px',
                              backgroundColor: bgColor,
                              color: color,
                              fontSize: '13px',
                            }}
                          >
                            {/* {['SFR', 'TAR', 'ACR'].includes(name?.toUpperCase())
                              ? rowData + '%'
                              : name == 'REV'
                                ? '₹' + rowData
                                : rowData} */}
                            {rowData}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Footer Section */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '13px',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
              Excellent (&gt;40%)
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#eab308' }} />
              Good (20-40%)
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#64748b' }} />
              Needs Improvement (&lt;20%)
            </Box>
          </Box>
        </>
      )}

      {/*TABLE SECTION */}
    </Paper>
  );
};

export default React.memo(TeamPerfomanceOverview);
